import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  XCircle,
  Rocket,
  Loader2,
  Mail,
  Users,
  Target,
  Calendar,
} from 'lucide-react'

import FbButton from 'components/FbUI/FbButton'
import {
  useCancelScheduledLaunch,
  useLaunchCampaign,
  useResumeCampaign,
} from 'features/campaigns/campaign_status_api'
import { useGetCampaignSchedule } from 'features/campaigns/campaign_schedule_api'
import { useGetCampaignLaunchReadiness } from 'features/campaigns/campaign_insights_api'
import { useGetCampaignSettings } from 'features/campaigns/campaign_settings_api'
import {
  useActiveCampaignValidationJob,
  useGetCampaignLeadReview,
  useStartCampaignValidationJob,
  useUpdateCampaignLeadReview,
} from 'features/campaigns/campaigns_api'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import {
  detectUnfilledVariables,
  fillVariables,
} from 'features/campaigns/email-variables'
import { useCampaignAITokensByContact } from 'features/campaigns/columns/useCampaignAITokens'
import { ScheduleLaunchModal } from 'features/campaigns/ScheduleLaunchModal'
import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'
import { getApiErrorDetail } from 'utils/getApiErrorDetail'
import { queryClient } from 'services/queryClient'

interface Props {
  campaignId: number
  campaign: Campaign
  onBack: () => void
  onLaunch: () => void
  stepNumber: number
  totalSteps: number
}

interface ChecklistItem {
  id: string
  title: string
  isComplete: boolean
  description: string
}

type ContactTokens = Record<string, string>

function buildTokens(contact: {
  full_name?: string | null
  email?: string | null
  company_name?: string | null
  title?: string | null
}): ContactTokens {
  const [firstName = '', ...rest] = (contact.full_name || '')
    .trim()
    .split(' ')
    .filter(Boolean)
  // Always emit every supported token, falling back to empty string for
  // missing fields. Mirrors the server resolver (which fills missing optional
  // fields with "") so ``detectUnfilledVariables`` does not flag e.g.
  // ``{COMPANY}`` as unfilled when a contact simply has no company on file.
  return {
    FIRST_NAME: firstName,
    LAST_NAME: rest.join(' '),
    EMAIL: contact.email ?? '',
    COMPANY: contact.company_name ?? '',
    TITLE: contact.title ?? '',
  }
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  return (
    <div className="flex items-center gap-4 border-b border-line py-4 last:border-b-0">
      <div className="shrink-0">
        {item.isComplete ? (
          <CheckCircle2 className="size-5 text-success" />
        ) : (
          <XCircle className="size-5 text-error" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-fg">{item.title}</div>
        <div
          className={
            item.isComplete ? 'text-xs text-fg-secondary' : 'text-xs text-error'
          }
        >
          {item.description}
        </div>
      </div>
    </div>
  )
}

function formatScheduleDescription(
  schedule:
    | {
        monday?: boolean
        tuesday?: boolean
        wednesday?: boolean
        thursday?: boolean
        friday?: boolean
        saturday?: boolean
        sunday?: boolean
        start_time?: string
        end_time?: string
      }
    | null
    | undefined
): string {
  if (!schedule) return 'Not configured'

  const days: string[] = []
  if (schedule.monday) days.push('Mon')
  if (schedule.tuesday) days.push('Tue')
  if (schedule.wednesday) days.push('Wed')
  if (schedule.thursday) days.push('Thu')
  if (schedule.friday) days.push('Fri')
  if (schedule.saturday) days.push('Sat')
  if (schedule.sunday) days.push('Sun')

  const daysStr = days.length > 0 ? days.join(', ') : 'No days'
  const timeStr =
    schedule.start_time && schedule.end_time
      ? `${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`
      : 'No time set'

  return `${daysStr}, ${timeStr}`
}

function formatTime(time: string): string {
  if (!time) return ''
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

export function WizardLaunchStep({
  campaignId,
  campaign,
  onBack,
  onLaunch,
  stepNumber,
  totalSteps,
}: Props) {
  const {
    data: readiness,
    isLoading: readinessLoading,
    refetch: refetchReadiness,
  } = useGetCampaignLaunchReadiness(campaignId)
  const { data: schedule } = useGetCampaignSchedule(campaignId)
  const { data: settings } = useGetCampaignSettings(campaignId)
  const { data: leadReview, isLoading: leadReviewLoading } =
    useGetCampaignLeadReview(campaignId)
  const startBulk = useStartCampaignValidationJob(campaignId)
  const activeJob = useActiveCampaignValidationJob(campaignId)
  const updateSelection = useUpdateCampaignLeadReview(campaignId)
  // Fast bulk jobs (<1s) finish before the 2s validation-job poller
  // observes pending/in_progress; hold the running state on optimistically
  // so the spinner is visible regardless.
  const [optimisticBulkRunning, setOptimisticBulkRunning] = useState(false)
  const includedNeedsValidation = useMemo(
    () =>
      leadReview?.results.filter(
        (r) => r.qa_status === 'needs_validation' && r.is_included
      ) ?? [],
    [leadReview?.results]
  )
  const includedNeedsValidationCount = includedNeedsValidation.length
  const polledBulkRunning =
    !!activeJob.data &&
    (activeJob.data.status === 'pending' ||
      activeJob.data.status === 'in_progress')
  const isBulkRunning = polledBulkRunning || optimisticBulkRunning
  const { data: sequence, isLoading: sequenceLoading } =
    useGetCampaignSequence(campaignId)
  const aiTokensByContact = useCampaignAITokensByContact(campaignId)
  const bisonCampaignId = readiness?.bison_campaign_id
  const launchMutation = useLaunchCampaign(campaignId)
  const resumeMutation = useResumeCampaign(campaignId)
  const cancelScheduleMutation = useCancelScheduledLaunch(
    bisonCampaignId ?? 0,
    campaignId
  )
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  // Bump to force ScheduleLaunchModal to remount each time it opens so
  // `useState(...)` initializers re-derive from the latest props.
  const [scheduleModalNonce, setScheduleModalNonce] = useState(0)
  const openScheduleModal = () => {
    setScheduleModalNonce((n) => n + 1)
    setIsScheduleModalOpen(true)
  }
  const contactsForVariableCheck = useMemo(
    () =>
      (leadReview?.results ?? []).filter(
        (contact) => contact.is_included && contact.email
      ),
    [leadReview?.results]
  )
  const emailVariableIssues = useMemo(() => {
    const steps = sequence?.steps ?? []
    return contactsForVariableCheck.flatMap((contact) =>
      steps
        .map((step) => {
          const tokens = {
            ...buildTokens(contact),
            ...(aiTokensByContact[contact.contact_id] ?? {}),
          }
          const subject = fillVariables(step.email_subject, tokens)
          const body = fillVariables(step.email_body, tokens, {
            escapeValues: true,
          })
          const warnings = Array.from(
            new Set([
              ...detectUnfilledVariables(subject),
              ...detectUnfilledVariables(body),
            ])
          )
          return { contact, step, warnings }
        })
        .filter((item) => item.warnings.length > 0)
    )
  }, [aiTokensByContact, contactsForVariableCheck, sequence?.steps])
  const emailVariableCheckReady = !leadReviewLoading && !sequenceLoading
  const hasEmailVariableIssues = emailVariableIssues.length > 0

  const checklistItems: ChecklistItem[] = useMemo(() => {
    const stepCount = readiness?.sequence.step_count || 0
    const leadCount = readiness?.leads.count || 0
    const hasSchedule = readiness?.schedule.is_complete ?? false

    return [
      {
        id: 'sequence',
        title: 'Email Sequence',
        isComplete: stepCount > 0,
        description: readiness?.sequence.description || 'No steps configured',
      },
      {
        id: 'leads',
        title: 'Leads Synced',
        isComplete: leadCount > 0,
        description: readiness?.leads.description || 'No leads synced',
      },
      {
        id: 'sender-emails',
        title: 'Sender Emails',
        isComplete: readiness?.sender_emails.is_complete ?? false,
        description:
          readiness?.sender_emails.description || 'No sender emails attached',
      },
      {
        id: 'schedule',
        title: 'Send Schedule',
        isComplete: hasSchedule,
        description: hasSchedule
          ? formatScheduleDescription(schedule)
          : 'No schedule configured',
      },
      {
        id: 'email-variables',
        title: 'Email Variables',
        isComplete: emailVariableCheckReady && !hasEmailVariableIssues,
        description: !emailVariableCheckReady
          ? 'Checking email variables...'
          : hasEmailVariableIssues
            ? `${emailVariableIssues.length} email${emailVariableIssues.length === 1 ? '' : 's'} have variables that could not be filled`
            : 'All email variables can be filled',
      },
      {
        id: 'all-validated',
        title: 'All included contacts are validated',
        isComplete: includedNeedsValidationCount === 0 && !isBulkRunning,
        description:
          includedNeedsValidationCount === 0
            ? 'All included contacts have verified email deliverability.'
            : isBulkRunning
              ? 'Validation in progress…'
              : `${includedNeedsValidationCount} included contact${
                  includedNeedsValidationCount !== 1 ? 's' : ''
                } need${includedNeedsValidationCount === 1 ? 's' : ''} validation`,
      },
    ]
  }, [
    emailVariableCheckReady,
    emailVariableIssues.length,
    hasEmailVariableIssues,
    includedNeedsValidationCount,
    isBulkRunning,
    readiness,
    schedule,
  ])

  const allComplete = checklistItems.every((item) => item.isComplete)
  const stepCount = readiness?.sequence.step_count || 0
  const targetCount = campaign.contact_company_count || 0
  // Recipient count must match what's actually sent: lead-review's
  // `summary.included` is the live source of truth (same query the wizard
  // sidebar reads). EmailBison's `readiness.leads.count` only updates on
  // sync, and `campaign.contact_count` is a denormalized aggregate — both
  // can lag mid-build (FIR-7242). Fall through to them only when lead-review
  // hasn't loaded yet.
  const recipientCount =
    leadReview?.summary.included ??
    readiness?.leads.count ??
    campaign.contact_count ??
    0
  const campaignStatus = settings?.status || campaign.workflow_status
  const isPaused =
    campaignStatus === 'paused' ||
    campaign.workflow_status === WORKFLOW_STATUS.PAUSED
  const isActive =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaign.workflow_status === WORKFLOW_STATUS.ACTIVE
  const isComplete =
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign.workflow_status === WORKFLOW_STATUS.COMPLETE
  const isScheduled = campaign.workflow_status === WORKFLOW_STATUS.SCHEDULED
  const hasAlreadyLaunched = isPaused || isActive || isComplete
  const shouldBlockForEmailVariables =
    !isActive &&
    !isComplete &&
    (!emailVariableCheckReady || hasEmailVariableIssues)
  const primaryActionLabel = isPaused ? 'Resume Campaign' : 'Launch Now'
  // Track the full click→mutation lifecycle, not just the mutation itself —
  // the readiness refetch before the mutation can take several seconds, and
  // without this the button looks idle during that window.
  const [isSubmitting, setIsSubmitting] = useState(false)
  const primaryActionPending =
    isSubmitting ||
    (isPaused ? resumeMutation.isPending : launchMutation.isPending)
  const canScheduleLaunch =
    !hasAlreadyLaunched &&
    allComplete &&
    !!bisonCampaignId &&
    !shouldBlockForEmailVariables
  const scheduleTimezone = schedule?.timezone || 'UTC'

  const handlePrimaryAction = async () => {
    if (isActive || isComplete) {
      onLaunch()
      return
    }

    setIsSubmitting(true)
    try {
      const latestReadiness = await refetchReadiness()
      if (!latestReadiness.data?.all_complete) {
        toast.error('Please complete the missing setup items before launching')
        return
      }
      if (!latestReadiness.data?.bison_campaign_id) {
        toast.error('Campaign is still syncing. Refresh and try again.')
        return
      }
      if (shouldBlockForEmailVariables) {
        toast.error('Fix unfilled email variables before launching')
        return
      }
      if (includedNeedsValidationCount > 0) {
        toast.error(
          'Validate or exclude unvalidated contacts before launching.'
        )
        return
      }

      try {
        if (isPaused) {
          await resumeMutation.mutateAsync(
            latestReadiness.data.bison_campaign_id
          )
          toast.success('Campaign resumed successfully!')
        } else {
          await launchMutation.mutateAsync(
            latestReadiness.data.bison_campaign_id
          )
          toast.success('Campaign launched successfully!')
        }
        onLaunch()
      } catch (error: unknown) {
        toast.error(
          getApiErrorDetail(
            error,
            isPaused ? 'Failed to resume campaign' : 'Failed to launch campaign'
          )
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelScheduled = async () => {
    try {
      await cancelScheduleMutation.mutateAsync()
      toast.success('Scheduled launch cancelled')
    } catch (error: unknown) {
      toast.error(getApiErrorDetail(error, 'Failed to cancel scheduled launch'))
    }
  }

  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Preview & Launch"
        description="Review your campaign configuration and launch when ready."
      />

      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="mx-auto max-w-3xl space-y-6">
          {readinessLoading && (
            <div className="rounded-xl border border-line bg-surface p-4 text-sm text-fg-secondary">
              Refreshing launch readiness from EmailBison...
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <LaunchSummaryCard
              icon={Target}
              label="Targets"
              value={targetCount > 0 ? String(targetCount) : '—'}
              sublabel={targetCount === 1 ? 'target' : 'targets'}
            />
            <LaunchSummaryCard
              icon={Users}
              label="Recipients"
              value={recipientCount > 0 ? String(recipientCount) : 'None'}
              sublabel={recipientCount === 1 ? 'recipient' : 'recipients'}
            />
            <LaunchSummaryCard
              icon={Mail}
              label="Email Steps"
              value={String(stepCount)}
              sublabel={stepCount === 1 ? 'step' : 'steps'}
            />
          </div>

          <div className="rounded-xl border border-line bg-surface p-6">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-fg-secondary">
              Pre-launch Checklist
            </h2>
            <div className="divide-y divide-line">
              {checklistItems.map((item) => (
                <ChecklistRow key={item.id} item={item} />
              ))}
            </div>
            {includedNeedsValidationCount > 0 && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-bg px-3 py-2">
                <p className="text-sm text-fg">
                  {isBulkRunning && activeJob.data
                    ? `Validating… ${activeJob.data.processed_count} / ${activeJob.data.contact_count}`
                    : `${includedNeedsValidationCount} included contact${
                        includedNeedsValidationCount !== 1 ? 's' : ''
                      } need${
                        includedNeedsValidationCount === 1 ? 's' : ''
                      } validation.`}
                </p>
                <div className="flex gap-2">
                  <FbButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={isBulkRunning || startBulk.isPending}
                    onClick={() => {
                      setOptimisticBulkRunning(true)
                      startBulk.mutate(undefined, {
                        onSuccess: async () => {
                          // Hold the spinner until lead-review reflects the
                          // worker's verdicts, so rows move out of the
                          // Needs-Validation bucket exactly when the spinner
                          // clears.
                          await new Promise((r) => setTimeout(r, 1500))
                          await queryClient.refetchQueries({
                            queryKey: ['campaign-lead-review', campaignId],
                          })
                          setOptimisticBulkRunning(false)
                        },
                        onError: (err: unknown) => {
                          setOptimisticBulkRunning(false)
                          const e = err as {
                            response?: { status?: number }
                          }
                          if (e?.response?.status === 409) {
                            toast.info('Already validating')
                          } else {
                            toast.error('Failed to start validation')
                          }
                        },
                      })
                    }}
                  >
                    Validate all ({includedNeedsValidationCount})
                  </FbButton>
                  <FbButton
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isBulkRunning}
                    onClick={() => {
                      updateSelection.mutate({
                        contact_ids: includedNeedsValidation.map(
                          (r) => r.contact_id
                        ),
                        is_included: false,
                      })
                    }}
                  >
                    Exclude all ({includedNeedsValidationCount})
                  </FbButton>
                </div>
              </div>
            )}
          </div>

          {!hasAlreadyLaunched && !allComplete && (
            <div className="rounded-xl border border-warning-border bg-warning-bg p-4">
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 size-5 shrink-0 text-warning" />
                <div>
                  <p className="text-sm font-medium text-warning">
                    Complete all checklist items to launch
                  </p>
                  <p className="mt-1 text-xs text-warning">
                    All items must be completed before you can launch your
                    campaign.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isActive && !isComplete && hasEmailVariableIssues && (
            <div className="rounded-xl border border-warning-border bg-warning-bg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" />
                <div>
                  <p className="text-sm font-medium text-warning">
                    Fix email variables before launch
                  </p>
                  <p className="mt-1 text-xs text-warning">
                    First issue: {emailVariableIssues[0].contact.full_name},
                    step {emailVariableIssues[0].step.order}:{' '}
                    {emailVariableIssues[0].warnings.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isPaused && (
            <div className="rounded-xl border border-warning-border bg-warning-bg p-4">
              <div className="flex items-start gap-3">
                <Rocket className="mt-0.5 size-5 shrink-0 text-warning" />
                <div>
                  <p className="text-sm font-medium text-warning">
                    Campaign is paused
                  </p>
                  <p className="mt-1 text-xs text-warning">
                    This campaign has already been launched and is currently
                    paused. Resume it to continue sending on the next available
                    window.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isActive && (
            <div className="rounded-xl border border-brand/20 bg-brand/5 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-brand" />
                <div>
                  <p className="text-sm font-medium text-fg">
                    Campaign already launched
                  </p>
                  <p className="mt-1 text-xs text-fg-secondary">
                    This campaign is already sending or queued to send. Use the
                    dashboard to monitor performance and control campaign
                    status.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="rounded-xl border border-line bg-surface-alt p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-brand" />
                <div>
                  <p className="text-sm font-medium text-fg">
                    Campaign already completed
                  </p>
                  <p className="mt-1 text-xs text-fg-secondary">
                    This campaign has already finished sending. Use the
                    dashboard to review results and recipient activity.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!hasAlreadyLaunched &&
            !isScheduled &&
            campaign.auto_launch_failure_reason && (
              <div className="rounded-xl border border-error/30 bg-error-bg p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="mt-0.5 size-5 shrink-0 text-error" />
                  <div>
                    <p className="text-sm font-medium text-error">
                      {campaign.auto_launch_attempted_at
                        ? `Auto-launch failed on ${formatScheduledLaunchAt(
                            campaign.auto_launch_attempted_at,
                            scheduleTimezone
                          )}`
                        : 'Auto-launch failed'}
                    </p>
                    <p className="mt-1 text-xs text-error">
                      {campaign.auto_launch_failure_reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {!hasAlreadyLaunched && !isScheduled && allComplete && (
            <div className="rounded-xl border border-success/30 bg-success-bg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
                <div>
                  <p className="text-sm font-medium text-success">
                    Ready to launch
                  </p>
                  <p className="mt-1 text-xs text-success">
                    All checklist items are complete. Launch now or schedule the
                    campaign for a later date.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isScheduled && campaign.scheduled_launch_at && (
            <div className="rounded-xl border border-brand/20 bg-brand/5 p-4">
              <div className="flex items-start gap-3">
                <CalendarClock className="mt-0.5 size-5 shrink-0 text-brand" />
                <div>
                  <p className="text-sm font-medium text-fg">
                    Scheduled for{' '}
                    {formatScheduledLaunchAt(
                      campaign.scheduled_launch_at,
                      scheduleTimezone
                    )}
                  </p>
                  <p className="mt-1 text-xs text-fg-secondary">
                    This campaign will launch automatically at the scheduled
                    time in <strong>{scheduleTimezone}</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <ArrowLeft className="size-4" />
          Back
        </FbButton>
        <div className="flex items-center gap-3">
          {isScheduled ? (
            <>
              <FbButton
                type="button"
                variant="outline"
                onClick={handleCancelScheduled}
                disabled={!bisonCampaignId || cancelScheduleMutation.isPending}
                className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
              >
                {cancelScheduleMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
                Cancel scheduled launch
              </FbButton>
              <FbButton
                type="button"
                onClick={openScheduleModal}
                disabled={!bisonCampaignId}
                className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
              >
                <CalendarClock className="size-4" />
                Reschedule
              </FbButton>
            </>
          ) : (
            <>
              {canScheduleLaunch && (
                <FbButton
                  type="button"
                  variant="outline"
                  onClick={openScheduleModal}
                  className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
                >
                  <CalendarClock className="size-4" />
                  Schedule launch
                </FbButton>
              )}
              <FbButton
                type="button"
                onClick={handlePrimaryAction}
                disabled={
                  (!hasAlreadyLaunched && !allComplete) ||
                  primaryActionPending ||
                  readinessLoading ||
                  shouldBlockForEmailVariables ||
                  !bisonCampaignId
                }
                className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
              >
                {primaryActionPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {isPaused ? 'Resuming...' : 'Launching...'}
                  </>
                ) : (
                  <>
                    <Rocket className="size-4" />
                    {isActive || isComplete
                      ? 'View Dashboard'
                      : primaryActionLabel}
                  </>
                )}
              </FbButton>
            </>
          )}
        </div>
      </WizardStepFooter>

      {bisonCampaignId ? (
        <ScheduleLaunchModal
          key={scheduleModalNonce}
          show={isScheduleModalOpen}
          onHide={() => setIsScheduleModalOpen(false)}
          bisonCampaignId={bisonCampaignId}
          campaignId={campaignId}
          scheduleTimezone={scheduleTimezone}
          initialScheduledAt={isScheduled ? campaign.scheduled_launch_at : null}
        />
      ) : null}
    </div>
  )
}

function formatScheduledLaunchAt(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
    }).format(new Date(iso))
  } catch {
    return new Date(iso).toLocaleString()
  }
}

function LaunchSummaryCard({
  icon: Icon,
  label,
  value,
  sublabel,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  sublabel: string
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="size-4 text-brand" />
        <span className="text-xs text-fg-secondary">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-fg">{value}</p>
      <p className="mt-1 text-xs text-fg-secondary">{sublabel}</p>
    </div>
  )
}
